import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Nav from "./Nav";
import Footer from "./Footer";
import HreflangTags from "@/components/common/HreflangTags";
import useLangSync from "@/lib/useLangSync";

export default function Layout({ children, title, description, notFound = false }) {
    const { pathname } = useLocation();
    useLangSync();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);

    useEffect(() => {
        if (title) document.title = title;
        if (description) {
            let m = document.querySelector('meta[name="description"]');
            if (!m) {
                m = document.createElement("meta");
                m.setAttribute("name", "description");
                document.head.appendChild(m);
            }
            m.setAttribute("content", description);
        }
    }, [title, description]);

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* notFound: the catch-all NotFound page and DetailNotFound's
                invalid-slug states must not publish a canonical, hreflang
                alternates, or og:url pointing at a URL that doesn't
                actually resolve to real content — noindex is handled
                separately by each page's own useNoIndex() call. */}
            <HreflangTags suppressCanonical={notFound} />
            <Nav />
            <main className="pt-16">{children}</main>
            <Footer />
        </div>
    );
}

export const Section = ({ children, className = "", id, ...rest }) => (
    <section id={id} className={`max-w-[1400px] mx-auto px-6 sm:px-10 ${className}`} {...rest}>
        {children}
    </section>
);

export const PageHero = ({ eyebrow, title, lead, i18nKey, testId = "page-hero" }) => {
    const { t } = useTranslation();
    const localized = i18nKey ? {
        eyebrow: t(`pages.${i18nKey}.eyebrow`),
        title: (<>{t(`pages.${i18nKey}.titleA`)}{t(`pages.${i18nKey}.titleB`) ? <><br />{t(`pages.${i18nKey}.titleB`)}</> : null}</>),
        lead: t(`pages.${i18nKey}.lead`),
    } : null;
    const _eyebrow = localized?.eyebrow ?? eyebrow;
    const _title = localized?.title ?? title;
    const _lead = localized?.lead ?? lead;
    return (
    <Section className="pt-32 sm:pt-40 pb-20" data-testid={testId}>
        <div className="max-w-4xl">
            {_eyebrow && <p className="label mb-8">{_eyebrow}</p>}
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
                {_title}
            </h1>
            {_lead && (
                <p className="prose-body text-lg sm:text-xl mt-10 max-w-2xl">
                    {_lead}
                </p>
            )}
        </div>
    </Section>);
};
